import CookieService from '@/services/isomorphic/CookieService';
import type { ICookie } from '@/services/models/Cookie';
import { isNullOrEmpty } from '@/utils/null-utils';
import { BailoutToCSRError } from 'next/dist/shared/lib/lazy-dynamic/bailout-to-csr';
import { useCallback, useSyncExternalStore } from 'react';

/**
 * The options for setting a cookie using the `setCookie`
 * function returned by the `useCookie` hook.
 */
type SetCookieOptions = Pick<
  ICookie,
  'value' | 'domain' | 'path' | 'expires' | 'sameSite' | 'secure' | 'maxAge'
>;

/** The return type of the {@link useCookie} hook. */
export type UseCookieHookResult = [
  /** The current value of the cookie, or `null` if not set. */
  value: string | null,
  /**
   * Sets or deletes the cookie. If `null` is passed, the cookie will be deleted.
   * @param options - The options for setting the cookie, or `null` to delete it.
   */
  setCookie: (options: SetCookieOptions | null) => void
];

/**
 * A custom hook for getting and setting a cookie value reactively.
 * @param name - The name of the cookie to get/set.
 * @throws {BailoutToCSRError} If used on the server. This will fallback to the nearest
 * `Suspense` boundary and resume rendering on the client.
 * @returns A tuple containing the current cookie value (or `null` if not set),
 * and a function to set the cookie value (or delete it if `null` is passed).
 */
export function useCookie(name: string): UseCookieHookResult {
  if (typeof window === 'undefined') {
    throw new BailoutToCSRError(
      'useCookie was called on the server. This hook only works in a client component.'
    );
  }

  const subscribe = useCallback(
    function subscribeToCookieStore(listener: () => void) {
      if (!('cookieStore' in window)) {
        // Fallback for browsers that do not support the Cookie Store API.
        // This will not be reactive, but changes will still apply after re-rendering
        // or refreshing the page. Note that this may also cause UI tearing.
        return () => {};
      }

      const handler = (e: CookieChangeEvent): void => {
        if (e.changed.some((cookie) => cookie.name === name)) {
          listener();
        }
      };

      window.cookieStore.addEventListener('change', handler);
      return () => {
        window.cookieStore.removeEventListener('change', handler);
      };
    },
    [name]
  );

  const getCookie = useCallback(
    function getCookie() {
      return CookieService.tryGet(name)?.value ?? null;
    },
    [name]
  );

  const value = useSyncExternalStore(subscribe, getCookie, getCookie);

  const setCookie = useCallback(
    (options: SetCookieOptions | null) => {
      if (isNullOrEmpty(options)) {
        CookieService.delete(name);
      } else
        CookieService.set({
          key: name,
          ...options,
          expires: options.expires?.toUTCString()
        });
    },
    [name]
  );

  return [value, setCookie];
}
